import Vue from 'vue';
import * as storage from '../../utils/storage';
import load from '../../utils/fetch';
import { convertFromAssetDb } from '../../utils/assetdb_utils';
import { getAppVersion } from '../../utils/env.js';

const defaultUser = {
  name: 'demo',
  token: '2e4ad188153a7a4f88cb4bba4e3819b534a8b812'
};

const persisted = storage.load('assetdb') || {
  name: defaultUser.name,
  token: defaultUser.token,
  assets: [],
  custom: [],
  mode: 'grid'
};

function persist () {
  storage.persist('assetdb', {
    custom: state.custom,
    assets: state.assets,
    token: state.token,
    name: state.name,
    mode: state.mode,
    updated: new Date().toISOString(),
    version: getAppVersion(),
  });
}

// Initial state
const state = {
  custom: persisted.custom || [],
  assets: persisted.assets || [],
  token: persisted.token || '',
  name: persisted.name || '',
  loading: false,
  mode: persisted.mode || 'grid'
};

// getters
const getters = {
  isLoggedIn: (state) => {
    return state.token !== '' && state.token !== defaultUser.token;
  },
  asset: (state) => (name) => {
    // eslint-disable-next-line eqeqeq
    const s = state.assets.filter((a) => a.id == name);
    if (s && s.length > 0) return Object.assign({}, JSON.parse(JSON.stringify(s[0])));
    return null;
  },
  custom: (state) => (name) => {
    const index = Number(name);
    if (state.custom.length > 0 && index < state.custom.length) {
      return Object.assign({}, JSON.parse(JSON.stringify(state.custom[name])));
    }
    return null;
  },
  getAuthToken: (state) => async (name) => {
    const s = state.assets.filter((a) => {
      // eslint-disable-next-line eqeqeq
      return a.id == name;
    });
    if (s && s.length > 0) {
      const a = s[0];

      const response = await load(a.updateUrl, {
        headers: {
          Authorization: `Token ${state.token}`
        }
      });

      const data = await response.json();
      return data.drmConfiguration.authToken;
    }
    return null;
  }
};

// actions
const actions = {
  mode ({ commit }, data) {
    commit('mode', data);
  },
  save ({ commit }, data) {
    commit('save', data);
    const id = data.id;
    if (id === null || id === undefined) {
      return state.custom.length - 1;
    }
    return Number(id);
  },
  delete ({ commit }, data) {
    commit('delete', data);
  },
  async login ({ commit }, data) {
    commit('logout');

    const url = 'https://assets.castlabs.com/api/login/';
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (response.status !== 200) {
      throw new Error(`Bad status code: ${response.status}.`);
    }

    const json = await response.json();

    if (!json.token) {
      throw new Error('Failed to login. No token in response.');
    }

    commit('user', json);
  },
  logout ({ commit, dispatch }) {
    commit('logout');
    commit('configs', []);
    commit('user', defaultUser);
    dispatch('refresh');
  },
  async refresh ({ state, commit }) {
    try {
      if (!state.token) {
        throw new Error('Auth token missing.');
      }

      commit('loading', true);

      const url = 'https://assets.castlabs.com/api/configs/';
      const response = await fetch(url, {
        headers: {
          Authorization: `Token ${state.token}`,
        },
      });

      if (response.status < 200 || response.status >= 400) {
        throw new Error(`Bad status code: ${response.status}.`);
      }

      const json = await response.json();

      if (!json.configs) {
        throw new Error('Failed to load configs from asset DB. No configs in response.');
      }

      commit('configs', json.configs);
    } catch (error) {
      console.error('Failed to re-load assets.', error);
      // This may be due to an expired auth token (which expires after 1 week)
      // and if it is then a new login will fix that.
      Vue.$toast.open({
        message: 'Failed to refresh assets, try to logout and login again.',
        duration: 6000,
        position: 'top',
        type: 'error',
      });
    } finally {
      commit('loading', false);
    }
  }
};

// mutations
const mutations = {
  user (state, data) {
    state.token = data.token;
    state.name = data.name;
    persist();
  },
  delete (state, data) {
    if (data && data.id !== null && data.id !== undefined) {
      state.custom.splice(data.id, 1);
    }
  },
  save (state, data) {
    if (!data) return;
    const cfg = data.cfg;
    let id = data.id;
    cfg.metadata = cfg.metadata || {};
    if (id === null || id === undefined) {
      id = state.custom.length;
    }
    if (id < state.custom.length) {
      state.custom[id] = cfg;
    } else {
      state.custom.push(cfg);
    }
    persist();
  },
  loading (state, data) {
    state.loading = data;
  },
  mode (state, data) {
    state.mode = data;
    persist();
  },
  logout () {
    state.token = '';
    state.name = '';
    state.assets = [];
    persist();
  },
  configs (state, configs) {
    state.assets = configs.map((cfg) => {
      return convertFromAssetDb(cfg);
    }).filter((a) => a !== null);
    persist();
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
